import {
  GraphPersonAttributeKeys,
  GraphPersonGenderValues,
  type GraphChangeSetWrite,
  type GraphEntityType,
  type GraphNode
} from "@salesko/contracts";

// Union of every editable data key across all entity-type field specs. The draft
// always carries the full shape; each type only renders/diffs/writes the keys in
// its own spec (see nodeDataFieldSpecsByType). Keep this list and the exhaustive
// nodeDataFieldLabel switch in DetailPanel in sync — TS enforces a label per key.
export const nodeDataFieldKeys = [
  ...GraphPersonAttributeKeys,
  "industry",
  "size",
  "revenue",
  "website",
  "timeText"
] as const;

export type NodeDataFieldKey = (typeof nodeDataFieldKeys)[number];

export type NodeDataFieldKind = "text" | "date" | "select";

export type NodeDataFieldSpec = {
  key: NodeDataFieldKey;
  kind: NodeDataFieldKind;
  options?: readonly string[];
};

// Stable option value sets. Values are stored verbatim in node.data as strings.
// gender/industry get localized labels via fieldOptionLabel; size buckets are
// language-neutral and render their value directly.
export const genderOptionValues = GraphPersonGenderValues;
export const companySizeOptionValues = [
  "1-10",
  "11-50",
  "51-200",
  "201-500",
  "501-1000",
  "1001-5000",
  "5000+"
] as const;
export const industryOptionValues = [
  "technology",
  "finance",
  "healthcare",
  "manufacturing",
  "retail",
  "education",
  "media",
  "realEstate",
  "energy",
  "consulting",
  "government",
  "other"
] as const;

const personFieldSpecs: readonly NodeDataFieldSpec[] = [
  { key: "role", kind: "text" },
  { key: "company", kind: "text" },
  { key: "gender", kind: "select", options: genderOptionValues },
  { key: "birthday", kind: "date" },
  { key: "interests", kind: "text" },
  { key: "email", kind: "text" },
  { key: "phone", kind: "text" },
  { key: "location", kind: "text" },
  { key: "source", kind: "text" },
  { key: "stage", kind: "text" }
];

const organisationFieldSpecs: readonly NodeDataFieldSpec[] = [
  { key: "industry", kind: "select", options: industryOptionValues },
  { key: "size", kind: "select", options: companySizeOptionValues },
  { key: "revenue", kind: "text" },
  { key: "website", kind: "text" },
  { key: "location", kind: "text" },
  { key: "source", kind: "text" },
  { key: "stage", kind: "text" }
];

// event / opportunity / note keep the original flat field set — behavior unchanged.
const legacyFieldSpecs: readonly NodeDataFieldSpec[] = [
  { key: "role", kind: "text" },
  { key: "company", kind: "text" },
  { key: "stage", kind: "text" },
  { key: "source", kind: "text" },
  { key: "timeText", kind: "text" }
];

export const nodeDataFieldSpecsByType: Record<GraphEntityType, readonly NodeDataFieldSpec[]> = {
  person: personFieldSpecs,
  organisation: organisationFieldSpecs,
  event: legacyFieldSpecs,
  opportunity: legacyFieldSpecs,
  note: legacyFieldSpecs
};

export type NodeEditDraft = {
  dataFields: Record<NodeDataFieldKey, string>;
  evidence: string;
  label: string;
  summary: string;
};

export function nodeEditDraftFromNode(node: GraphNode | null): NodeEditDraft {
  return {
    dataFields: nodeDataFieldsFromNode(node),
    evidence: node?.evidence ?? "",
    label: node?.label ?? "",
    summary: node?.summary ?? ""
  };
}

export function hasNodeEditChanges(node: GraphNode, draft: NodeEditDraft) {
  const currentDataFields = normalizedNodeDataFields(nodeDataFieldsFromNode(node));
  const nextDataFields = normalizedNodeDataFields(draft.dataFields);
  const specs = nodeDataFieldSpecsByType[node.type];

  return (
    node.label !== draft.label.trim() ||
    (node.summary ?? "") !== draft.summary.trim() ||
    (node.evidence ?? "") !== draft.evidence.trim() ||
    specs.some((spec) => currentDataFields[spec.key] !== nextDataFields[spec.key])
  );
}

export function nodeEditChangeSetWrite(node: GraphNode, draft: NodeEditDraft): GraphChangeSetWrite | null {
  const label = draft.label.trim();
  if (!label || !hasNodeEditChanges(node, draft)) {
    return null;
  }

  const summary = draft.summary.trim();
  const evidence = draft.evidence.trim();
  const dataFields = normalizedNodeDataFields(draft.dataFields);
  const specs = nodeDataFieldSpecsByType[node.type];
  const nextData: GraphNode["data"] = { ...node.data };
  for (const spec of specs) {
    const value = dataFields[spec.key];
    if (value) {
      nextData[spec.key] = value;
      continue;
    }

    if (isEditableNodeDataValue(node.data[spec.key])) {
      delete nextData[spec.key];
    }
  }

  const { data: _oldData, evidence: _oldEvidence, summary: _oldSummary, ...baseNode } = node;
  const nextNode: GraphNode = {
    ...baseNode,
    data: nextData,
    label,
    ...(summary ? { summary } : {}),
    ...(evidence ? { evidence } : {})
  };

  return {
    reason: `Edit node: ${node.label}`.slice(0, 500),
    clientMutationId: `node-edit:${node.id}:${stableEditHash(
      `${label}\n${summary}\n${evidence}\n${specs.map((spec) => `${spec.key}:${dataFields[spec.key]}`).join("\n")}`
    )}`.slice(0, 128),
    commands: [
      {
        type: "upsert_node",
        node: nextNode
      }
    ]
  };
}

function nodeDataFieldsFromNode(node: GraphNode | null): Record<NodeDataFieldKey, string> {
  return Object.fromEntries(
    nodeDataFieldKeys.map((key) => [key, editableNodeDataValueToString(node?.data[key])])
  ) as Record<NodeDataFieldKey, string>;
}

function normalizedNodeDataFields(
  fields: Partial<Record<NodeDataFieldKey, string>> | undefined
): Record<NodeDataFieldKey, string> {
  return Object.fromEntries(nodeDataFieldKeys.map((key) => [key, fields?.[key]?.trim() ?? ""])) as Record<
    NodeDataFieldKey,
    string
  >;
}

function editableNodeDataValueToString(value: unknown) {
  return value === undefined || value === null ? "" : isEditableNodeDataValue(value) ? String(value) : "";
}

function isEditableNodeDataValue(value: unknown) {
  return value === undefined || value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

export function stableEditHash(value: string) {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }

  return (hash >>> 0).toString(36);
}
